import { lazy } from 'react'

import { CssBaseline, ThemeProvider } from '@mui/material'
import useEagerConnect from 'hooks/useEagerConnect'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'

import {
  Toast,
  ProtectedRoute,
  ProtectedCuratorRoute,
  SuspenseWithChunkError,
  Modal,
  Dialog,
} from 'components'
import theme from './theme'
import ModalProvider from 'components/Modal/ModalContext'
import Account from 'views/Account'
import useInitialSetup from 'hooks/useInitialSetup'
import Marketing from 'views/Marketing'
import CuratorLoginPage from 'views/Login/CuratorLoginPage'
import ProjectsV2 from './views/ProjectsV2'
import CreateEquityDeal from './views/CreateEquityDeal'
import Partners from './views/Partners'
import { useUserRoles } from './state/user/hooks'

const Debug = lazy(() => import('./views/Debug'))
// const Home = lazy(() => import('./views/Home'))
const Login = lazy(() => import('./views/Login'))
const VerifyOTP = lazy(() => import('./views/VerifyOTP'))
const NotFound = lazy(() => import('./views/NotFound'))
const Quests = lazy(() => import('./views/Quests'))
const Chains = lazy(() => import('./views/Chains'))
const Projects = lazy(() => import('./views/Projects'))
const Tokens = lazy(() => import('./views/Tokens'))
const Submission = lazy(() => import('./views/Submission'))
const Sales = lazy(() => import('./views/Projects/Sales'))
const Profile = lazy(() => import('./views/Profile'))
const CreateCurator = lazy(() => import('./views/CreateCurator'))

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Projects />
      </ProtectedRoute>
    ),
  },
  {
    path: 'login',
    element: <Login />,
  },
  {
    path: 'curators/login',
    element: <CuratorLoginPage />,
  },
  {
    path: 'otp',
    element: <VerifyOTP />,
  },
  {
    path: 'profile',
    element: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
  },

  {
    path: 'debug',
    element: <Debug />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
  {
    path: 'quests/*',
    element: (
      <ProtectedRoute>
        <Quests />
      </ProtectedRoute>
    ),
  },
  {
    path: 'chains/*',
    element: (
      <ProtectedRoute>
        <Chains />
      </ProtectedRoute>
    ),
  },
  {
    path: 'tokens/*',
    element: (
      <ProtectedRoute>
        <Tokens />
      </ProtectedRoute>
    ),
  },
  {
    path: 'account-manage/*',
    element: (
      <ProtectedRoute roles={['SYSTEM_ADMIN']}>
        <Account />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/*',
    element: (
      <ProtectedRoute>
        <Marketing />
      </ProtectedRoute>
    ),
  },
  {
    path: 'projects/*',
    element: (
      <ProtectedRoute>
        <Projects />
      </ProtectedRoute>
    ),
  },
  {
    path: 'sales/*',
    element: (
      <ProtectedRoute>
        <Sales />
      </ProtectedRoute>
    ),
  },
  {
    path: 'submission',
    element: (
      <ProtectedRoute>
        <Submission />
      </ProtectedRoute>
    ),
  },
  {
    path: 'deals/*',
    element: (
      <ProtectedRoute>
        <ProjectsV2 />
      </ProtectedRoute>
    ),
  },
  {
    path: 'curate/new-equity',
    element: (
      <ProtectedCuratorRoute>
        <CreateEquityDeal />
      </ProtectedCuratorRoute>
    ),
  },
  {
    path: 'partners/*',
    element: (
      <ProtectedRoute>
        <Partners />
      </ProtectedRoute>
    ),
  },
  {
    path: 'curators/create',
    element: (
      <ProtectedRoute roles={['SYSTEM_ADMIN']}>
        <CreateCurator />
      </ProtectedRoute>
    ),
  },
])

const curatorRouter = createBrowserRouter([
  {
    path: 'login',
    element: <Login />,
  },
  {
    path: 'curators/login',
    element: <CuratorLoginPage />,
  },
  {
    path: 'login',
    element: <Login />,
  },

  {
    path: 'otp',
    element: <VerifyOTP />,
  },
  {
    path: 'profile',
    element: (
      <ProtectedCuratorRoute>
        <Profile />
      </ProtectedCuratorRoute>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
  {
    path: 'submission',
    element: (
      <ProtectedCuratorRoute>
        <Submission />
      </ProtectedCuratorRoute>
    ),
  },
  {
    path: 'curate/new-equity',
    element: (
      <ProtectedCuratorRoute>
        <CreateEquityDeal />
      </ProtectedCuratorRoute>
    ),
  },
  {
    path: 'deals/*',
    element: (
      <ProtectedCuratorRoute>
        <ProjectsV2 />
      </ProtectedCuratorRoute>
    ),
  },
  {
    path: 'curators/create',
    element: (
      <ProtectedCuratorRoute>
        <CreateCurator />
      </ProtectedCuratorRoute>
    ),
  },
])

const App: React.FC = () => {
  useEagerConnect()
  useInitialSetup()

  const roles = useUserRoles()

  let routerData = router

  if (roles.includes('CURATOR')) {
    routerData = curatorRouter
  }

  return (
    <ThemeProvider theme={theme}>
      <ModalProvider>
        <CssBaseline />
        <SuspenseWithChunkError fallback={<div />}>
          <Toast />
          <Modal />
          <Dialog />
          <RouterProvider router={routerData} />
        </SuspenseWithChunkError>
      </ModalProvider>
    </ThemeProvider>
  )
}

export default App
